import React from 'react';
import { useTranslation } from 'react-i18next';
import { CloudServerOutlined } from '@ant-design/icons';
import { VMConfig, OperatingSystemOption } from '../../../types/vm.type';
import { PricingAddon, PricingPlan } from '../../../services/pricing.service';

interface Step3VMPricingProps {
  config: VMConfig;
  operatingSystems: OperatingSystemOption[];
  selectedPlan: PricingPlan | null;
  selectedAddons: PricingAddon[];
}

const formatCurrency = (amount?: number, currency?: string): string => {
  if (amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 2,
  }).format(amount);
};

const Step3VMPricing: React.FC<Step3VMPricingProps> = ({
  config,
  operatingSystems,
  selectedPlan,
  selectedAddons,
}) => {
  const { t } = useTranslation();
  const selectedOSName = operatingSystems.find((os) => os.id === config.os)?.name;
  const baseAmount = selectedPlan?.priceAmount ?? 0;
  const addonsAmount = selectedAddons.reduce((sum, addon) => sum + (addon.priceAmount || 0), 0);
  const totalAmount = baseAmount + addonsAmount;

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 dark:bg-dark-bg rounded-lg p-6">
        <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <CloudServerOutlined className="text-xl" />
          {t('virtualMachines.create.step3.review')}
        </h3>

        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.step1.name')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.name}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.step1.os')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{selectedOSName}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.pricing.planLabel')}</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {selectedPlan?.planName || '-'}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.pricing.instanceLabel')}</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {selectedPlan?.providerConfiguration?.instanceType || config.instanceType || '-'}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.step1.storage')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.storage} GB</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.step1.backups')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.backups ? t('common.yes') : t('common.no')}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.step1.monitoring')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.monitoring ? t('common.yes') : t('common.no')}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.pricing.specsLabel')}</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {selectedPlan?.includedResources?.vcpu ?? '-'} vCPU •{' '}
              {selectedPlan?.includedResources?.memoryMb ?? '-'} MB RAM
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.pricing.addonsLabel')}</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {selectedAddons.length > 0
                ? selectedAddons.map((addon) => addon.addonName).join(', ')
                : t('virtualMachines.create.pricing.noAddonsSelected')}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-gray-900 dark:text-white">{t('virtualMachines.create.pricing.estimatedMonthlyCost')}</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(totalAmount, selectedPlan?.currency)}
          </p>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('virtualMachines.create.pricing.priceFromPricingNotice')}
        </p>
      </div>
    </div>
  );
};

export default Step3VMPricing;
